import React, { useCallback, useState } from 'react';

import { AxiosError } from 'axios';

import { getIntlCurrency, Platform, SubscriptionTier } from '@invoice-simple/common';
import { FeatureName } from '@invoice-simple/feature-gate';
import {
  getCouponDiscount,
  getCouponSku,
  ISPaywallCoupon,
  PaywallCouponConfig,
  paywallCoupons
} from '@invoice-simple/is-coupon';

import { loadStripe } from '@stripe/stripe-js';

import { trackFacebookEvent } from 'src/analytics/facebook';
import { getTransactionTax } from 'src/apis/avalara';
import { getBillingAddress } from 'src/apis/billingAPI';
import { ISIntl } from 'src/i18n';
import alertModel from 'src/models/AlertModel';
import LocationModel from 'src/models/LocationModel';
import Subscription from 'src/models/SubscriptionModel';
import UserModel, { Cadence } from 'src/models/UserModel';
import { useOptimizelyFlag } from 'src/services/flags/hooks/useOptimizelyFlag';
import { AppStore } from 'src/stores/AppStore';
import { defaultsStore } from 'src/stores/DefaultsStore';
import { getFormattedDateString } from 'src/util/date';
import { wholePriceToFloat } from 'src/util/number';
import { redirectToStripeCheckout } from 'src/util/stripeCheckout';
import { BookkeepingTier, shouldOfferLocalizedPrices } from 'src/util/subscriptions';
import { getBookkeepingTierSku } from 'src/util/subscriptionSku';
import { titleizeAll } from 'src/util/Titleize';
import { getURLQueryParam, URLQueryParamKeys } from 'src/util/url';
import {
  getBookkeepingTierPrice,
  getPaywallCouponInfo,
  getSubscriptionSwitchType,
  isEndOfTermSwitch,
  maybeGetOldTierFromNewTier,
  SubscriptionSwitchType
} from '../utils';
import { messages } from '../utils/messages';

export interface UpgradeProps {
  store: AppStore;
  intl: ISIntl;
  selectedInterval: Cadence;
  shouldDisallowSwitch?: (destinationTier: SubscriptionTier, user: UserModel) => boolean;
}

interface StripeCheckoutOpts {
  toBeTransferred?: boolean;
  successUrl?: string;
  cancelUrl?: string;
  couponSku?: string;
  couponName?: ISPaywallCoupon;
  description?: string;
  price?: number;
  orderSku?: string;
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY || '');

const getCancelUrl = () => {
  const returnPath = getURLQueryParam(URLQueryParamKeys.RETURN_PATH);
  const origin = getURLQueryParam(URLQueryParamKeys.ORIGIN);

  if (returnPath) {
    return `${returnPath}${origin ? `?origin=${origin}` : ''}`;
  }

  return '/subscription';
};

const getSuccessReturnPath = (tier: SubscriptionTier) => {
  const feature = getURLQueryParam(URLQueryParamKeys.FEATURE);
  const successReturnPath = getURLQueryParam(URLQueryParamKeys.SUCCESS_RETURN_PATH);

  if (!successReturnPath) {
    return null;
  }

  switch (feature) {
    case FeatureName.AUTOMATIC_REVIEWS:
      return tier.includes(SubscriptionTier.PREMIUM) ? successReturnPath : null;
    default:
      break;
  }
  return successReturnPath;
};

const BOOKKEEPING_SUCCESS_URL = `/bookkeeping/link-accounts?${URLQueryParamKeys.BOOKKEEPING_SUCCESS}=true`;

const getSuccessUrl = (user: UserModel, tier: SubscriptionTier, includeBookkeeping?: boolean) => {
  if (user.isGuest) {
    return '/login';
  }

  if (includeBookkeeping) {
    return BOOKKEEPING_SUCCESS_URL;
  }

  const successReturnPath = getSuccessReturnPath(tier);
  if (successReturnPath) {
    return successReturnPath;
  }

  const returnPath = getURLQueryParam(URLQueryParamKeys.RETURN_PATH);
  return returnPath ?? undefined;
};

export const useUpgrade = ({
  store,
  intl,
  selectedInterval,
  shouldDisallowSwitch
}: UpgradeProps) => {
  const { formatMessage } = intl;
  const [switching, setSwitching] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [showBookkeepingModal, setShowBookkeepingModal] = useState(false);
  const [pendingUpgradeTier, setPendingUpgradeTier] = useState<SubscriptionTier | null>(null);
  const [pendingUpgradeOpts, setPendingUpgradeOpts] = useState<StripeCheckoutOpts | undefined>(
    undefined
  );
  const isAvalaraTaxEnabled = useOptimizelyFlag('avalara_tax').enabled;
  const isRegistrationEnabled = useOptimizelyFlag('registration_billing').enabled;
  const isBookkeepingEnabled = useOptimizelyFlag('bookkeeping').enabled;

  const { user, upgradeSubscription } = store;

  const hasBaseSubscription = user.isSubPaid;

  const isEligibleForBookkeepingUpsell = useCallback(() => {
    return isBookkeepingEnabled && !user.hasBookkeepingSubscription;
  }, [isBookkeepingEnabled, user.hasBookkeepingSubscription]);

  const isEligibleToPurchase = async (): Promise<boolean> => {
    if (!user.isGuest) {
      return true;
    }

    if (!isAvalaraTaxEnabled || !isRegistrationEnabled) {
      return true;
    }

    const billing = await getBillingAddress();

    return !!billing;
  };

  // Maps a tier to its modern bookkeeping-eligible equivalent.
  // PREMIUM_LEGACY users get the PREMIUM bookkeeping SKU and pricing.
  const getBookkeepingEligibleTier = (
    t: SubscriptionTier
  ): SubscriptionTier.ESSENTIALS | SubscriptionTier.PLUS | SubscriptionTier.PREMIUM | null => {
    if (t === SubscriptionTier.ESSENTIALS) return SubscriptionTier.ESSENTIALS;
    if (t === SubscriptionTier.PLUS) return SubscriptionTier.PLUS;
    if (t === SubscriptionTier.PREMIUM || t === SubscriptionTier.PREMIUM_LEGACY) {
      return SubscriptionTier.PREMIUM;
    }
    return null;
  };

  const executeUpgrade = async (
    tier: SubscriptionTier,
    stripeCheckoutOpts?: StripeCheckoutOpts,
    includeBookkeeping?: boolean
  ) => {
    const isEligible = await isEligibleToPurchase();

    if (!isEligible) {
      LocationModel.nav('signup', {
        [URLQueryParamKeys.REF]: URLQueryParamKeys.BILLING_ADDRESS,
        [URLQueryParamKeys.RETURN_PATH]: URLQueryParamKeys.BILLING_ADDRESS
      });
      return;
    }

    const disallowSwitch = !!shouldDisallowSwitch?.(tier, user);
    const subscriptionToSwitch = user.lastActivePaidWebSub();
    const mobileSubscription = user.lastActivePaidMobileSub();
    const isSwitching = !disallowSwitch && !!subscriptionToSwitch;
    // Hoisted above switchType so bookkeeping context informs the switch classification
    const shouldIncludeBookkeeping = includeBookkeeping || user.hasBookkeepingSubscription;
    // true only when the user is actively gaining bookkeeping (not already subscribed)
    const isAddingBookkeeping = !user.hasBookkeepingSubscription && (includeBookkeeping ?? false);
    // true only when the user is explicitly opting out of bookkeeping (currently subscribed, passing false)
    // undefined means "no choice made" — preserve existing bookkeeping, not removing it
    const isRemovingBookkeeping = user.hasBookkeepingSubscription && includeBookkeeping === false;
    const switchType = getSubscriptionSwitchType(
      subscriptionToSwitch?.tier || mobileSubscription?.tier || null,
      tier,
      isAddingBookkeeping,
      isRemovingBookkeeping
    );
    const endOfTerm = isEndOfTermSwitch(switchType);
    const newOrderSku = user.getGeoSubOrderSku({
      tier,
      cadence: selectedInterval
    });

    // Disallow mobile-only subscribers from using the web paywall
    if (!subscriptionToSwitch && mobileSubscription) {
      const storeName = user.isSubAndroid ? 'Google Play Store' : 'Apple App Store';
      alertModel.setAlert(
        'danger',
        formatMessage(messages.activeMobileSub, { store: storeName }),
        formatMessage(messages.activeMobileSubMessage)
      );

      LocationModel.scrollTop();

      user.events.trackAction('subscription-switch', {
        type: switchType,
        platform: Platform.WEB,
        status: 'failed',
        orderId: mobileSubscription.orderId,
        oldSku: mobileSubscription.orderSku,
        newSku: newOrderSku,
        changeDate: new Date(),
        interPlatform: true,
        price: wholePriceToFloat(user.getGeoSubAmount({ tier, cadence: selectedInterval })),
        currency: user.geoSubCurrencyCode,
        tier: tier,
        oldTier: mobileSubscription.tier,
        version: ''
      });

      return;
    }

    const oldTierVariant = maybeGetOldTierFromNewTier(tier);
    const currentUserTier = user.activeSubscriptionTier;
    if (currentUserTier) {
      const currentUserInterval = user.lastActivePaidWebSub(currentUserTier)?.planInterval;
      const isSubscribedToSelectedInterval = currentUserInterval === selectedInterval;
      const isSubscribedToSelectedTier = currentUserTier === tier;
      // Trying to change to the same tier and interval without adding bookkeeping
      // — just ignore and show no error message.
      // When isAddingBookkeeping is true, we must proceed even for same tier+interval.
      if (isSubscribedToSelectedInterval && isSubscribedToSelectedTier && !isAddingBookkeeping) {
        return;
      }
    }

    try {
      const couponName = stripeCheckoutOpts?.couponName;
      const coupon = couponName ? paywallCoupons[couponName] : undefined;

      // if user is switching from payments free tier, he will open stripe checkout instead of calling upgrade api
      if (isSwitching && !user.isSubTier(SubscriptionTier.PAYMENTS_TIER)) {
        setSwitching(true);

        const bookkeepingEligibleTier = getBookkeepingEligibleTier(tier);
        const orderSkuForSwitch =
          shouldIncludeBookkeeping && bookkeepingEligibleTier
            ? getBookkeepingTierSku({ tier: bookkeepingEligibleTier, cadence: selectedInterval })
            : newOrderSku;
        const switched = await switchSub({
          subscriptionToSwitch,
          newTier: tier,
          newOrderSku: orderSkuForSwitch,
          switchType,
          coupon: coupon,
          couponSku: stripeCheckoutOpts?.couponSku
        });

        if (switched) {
          await user.syncSubscriptions();

          if (includeBookkeeping) {
            window.location.assign(BOOKKEEPING_SUCCESS_URL);
            return;
          }

          let alertMessage: string | undefined;
          if (endOfTerm) {
            const isDowngrading = switchType === SubscriptionSwitchType.DOWNGRADE;
            alertMessage = formatMessage(
              getScheduledSwitchMessage(selectedInterval, isDowngrading),
              {
                tier: titleizeAll(oldTierVariant),
                date: getFormattedDateString(subscriptionToSwitch.expiryTimestamp!)
              }
            );
          } else {
            switch (tier) {
              case SubscriptionTier.PREMIUM_LEGACY:
                alertMessage = formatMessage(messages.switchSuccessAlertMessagePremiumLegacy);
                break;
              case SubscriptionTier.ESSENTIALS:
              case SubscriptionTier.STARTER:
                alertMessage = formatMessage(messages.switchSuccessAlertMessageStarter, {
                  docLimit: user.tierOneMonthlyQuota
                });
                break;
              case SubscriptionTier.PLUS:
                alertMessage = formatMessage(messages.switchSuccessAlertMessageStarter, {
                  docLimit: user.plusMonthlyQuota
                });
                break;
              default:
                alertMessage = formatMessage(messages.switchSuccessAlertMessageUnlimited);
            }
          }

          alertModel.setAlert(
            'success',
            formatMessage(
              selectedInterval === Cadence.MONTHLY
                ? messages.switchSuccessAlertTitleMonthly
                : messages.switchSuccessAlertTitleAnnual,
              {
                tier: titleizeAll(oldTierVariant)
              }
            ),
            alertMessage
          );

          LocationModel.scrollTop();
        }
        return;
      }

      setLoadingCheckout(true);

      const successUrl = getSuccessUrl(user, tier, includeBookkeeping);

      const bookkeepingEligibleTier = getBookkeepingEligibleTier(tier);
      const bookkeepingOrderSku =
        shouldIncludeBookkeeping && bookkeepingEligibleTier
          ? getBookkeepingTierSku({ tier: bookkeepingEligibleTier, cadence: selectedInterval })
          : undefined;

      const checkoutOpts = buildCheckoutOptions(
        bookkeepingOrderSku,
        bookkeepingEligibleTier,
        selectedInterval,
        user,
        stripeCheckoutOpts,
        couponName,
        coupon,
        intl
      );

      const effectiveTier =
        shouldIncludeBookkeeping && bookkeepingEligibleTier ? bookkeepingEligibleTier : tier;
      await openStripeCheckout(effectiveTier, {
        ...checkoutOpts,
        ...(checkoutOpts?.cancelUrl ? {} : { cancelUrl: getCancelUrl() }),
        ...(successUrl ? { successUrl } : {})
      });
    } catch (err) {
      const error = err as AxiosError<{ title?: string; message?: string }>;

      const defaultErrorTitle = isSwitching
        ? 'Error switching subscription'
        : 'Error creating checkout';
      const defaultErrorMessage = 'Please contact support.';
      if (error.response) {
        alertModel.setAlert(
          'danger',
          error.response.data?.title || defaultErrorTitle,
          error.response.data?.message || defaultErrorMessage
        );
      } else {
        alertModel.setAlert('danger', defaultErrorTitle, error.message || defaultErrorMessage);
      }
      LocationModel.scrollTop();
    } finally {
      setLoadingCheckout(false);
      setSwitching(false);
    }
  };

  const switchSub = async ({
    subscriptionToSwitch,
    newTier,
    newOrderSku,
    switchType,
    coupon,
    couponSku
  }: {
    subscriptionToSwitch: Subscription;
    newTier: SubscriptionTier;
    newOrderSku: string;
    switchType: SubscriptionSwitchType;
    coupon?: PaywallCouponConfig;
    couponSku?: string;
  }) => {
    const endOfTerm = isEndOfTermSwitch(switchType);
    const isDowngrading = switchType === SubscriptionSwitchType.DOWNGRADE;

    const downgradingMessage = formatMessage(messages.downgradeConfirmMessage, {
      oldTier: titleizeAll(
        subscriptionToSwitch.tier
          ? maybeGetOldTierFromNewTier(subscriptionToSwitch.tier)
          : 'Non-tier'
      ),
      newTier: titleizeAll(maybeGetOldTierFromNewTier(newTier))
    });
    const scheduledSwitchMessage = formatMessage(
      getScheduledSwitchMessage(selectedInterval, isDowngrading),
      {
        tier: titleizeAll(maybeGetOldTierFromNewTier(newTier)),
        date: getFormattedDateString(subscriptionToSwitch.expiryTimestamp!)
      }
    );
    const confirmPrompt = formatMessage(messages.confirmPrompt);
    const confirmationMessage = `${
      isDowngrading ? downgradingMessage.concat(' ') : ''
    }${scheduledSwitchMessage} ${confirmPrompt}`;

    // If end of term, only upgrade on confirmation
    if (endOfTerm ? window.confirm(confirmationMessage) : true) {
      await upgradeSubscription({
        subscriptionToSwitch,
        newOrderSku,
        switchType,
        coupon: coupon?.name,
        couponSku: couponSku,
        newPrice: user.getGeoSubAmount({ tier: newTier, cadence: selectedInterval, coupon })
      });

      return true;
    }
    return false;
  };

  const openStripeCheckout = async (tier: SubscriptionTier, opts?: StripeCheckoutOpts) => {
    const stripe = await stripePromise;
    if (!stripe) {
      return;
    }
    const productTitle = formatMessage(
      selectedInterval === Cadence.MONTHLY
        ? messages.monthlyTierSubscriptionTitle
        : messages.annualTierSubscriptionTitle,
      { tier: titleizeAll(maybeGetOldTierFromNewTier(tier)) }
    );
    const price = opts?.price ?? user.getGeoSubAmount({ tier, cadence: selectedInterval });
    const orderSku = opts?.orderSku ?? user.getGeoSubOrderSku({ tier, cadence: selectedInterval });

    const tax = isAvalaraTaxEnabled ? await getTransactionTax(price / 100, user) : undefined;

    // Using CustomizeProduct because InitiateCheckout is already being used
    trackFacebookEvent('track', 'CustomizeProduct', {
      value: price,
      order_sku: orderSku,
      currency: user.geoSubCurrencyCode
    });
    const origin = getURLQueryParam(URLQueryParamKeys.ORIGIN) ?? undefined;

    await redirectToStripeCheckout({
      store,
      productTitle,
      origin,
      description: opts?.description,
      price,
      tax,
      orderSku,
      successUrl: opts?.successUrl,
      cancelUrl: opts?.cancelUrl ?? '/subscription',
      couponSku: opts?.couponSku,
      couponName: opts?.couponName,
      tier,
      cadence: selectedInterval,
      toBeTransferred: opts?.toBeTransferred
    });
  };

  const getScheduledSwitchMessage = (cadence: Cadence, isDowngrading: boolean) => {
    if (isDowngrading) {
      return messages.switchedAtEndofCycle;
    }
    return cadence === Cadence.ANNUAL
      ? messages.scheduledSwitchNoticeAnnual
      : messages.scheduledSwitchNoticeMonthly;
  };

  const OriginalPrice = ({
    tier,
    shouldHidePerYear
  }: {
    tier: SubscriptionTier;
    shouldHidePerYear?: boolean;
  }) => {
    const isCadOrAud = ['CAD', 'AUD'].includes(user.geoSubCurrencyCode);
    const hidePerYear = shouldHidePerYear || (shouldOfferLocalizedPrices() && isCadOrAud);

    return (
      <span className="mr-3 leading-6 text-gray-600 line-through strikethrough-price">
        {getIntlCurrency({
          number: (user.getGeoSubAmount({ tier, cadence: Cadence.MONTHLY }) / 100) * 12,
          currencyCode: user.geoSubCurrencyCode,
          localeCode: defaultsStore.locale.name
        })}
        {!hidePerYear && <span className="text-2xl">/yr</span>}
      </span>
    );
  };

  const FinalPrice = ({
    tier,
    isTieredMatrix,
    coupon
  }: {
    tier: SubscriptionTier;
    isTieredMatrix?: boolean;
    coupon?: PaywallCouponConfig;
  }) => (
    <span>
      {getIntlCurrency({
        number: user.getGeoSubAmount({ tier, coupon, cadence: selectedInterval }) / 100,
        currencyCode: user.geoSubCurrencyCode,
        localeCode: defaultsStore.locale.name
      })}
      {!isTieredMatrix ? (
        <span className="text-2xl font-medium text-gray-500">
          /{selectedInterval === Cadence.MONTHLY ? 'mo' : 'yr'}
        </span>
      ) : null}
    </span>
  );

  const isCurrentPlan = (tier: SubscriptionTier) => {
    const currentPlanTier = user.activeSubscriptionTier;
    if (currentPlanTier === tier && user.lastActivePaidWebSub()?.isWebPaymentsTier) return true;
    const currentPlanInterval = user.lastActivePaidWebSub()?.planInterval;

    return currentPlanTier === tier && currentPlanInterval === selectedInterval;
  };

  const getCouponPrices = useCallback(
    (coupon: PaywallCouponConfig, tier: SubscriptionTier, cadence: Cadence) => {
      const originalPrice = user.getGeoSubAmount({
        tier,
        cadence
      });
      const discountPrice = user.getGeoSubAmount({
        tier,
        cadence,
        coupon
      });

      const originalPriceDisplay = originalPrice / 100;
      const discountPriceDisplay = discountPrice / 100;
      const isDiscounted = discountPrice < originalPrice;

      return {
        originalPrice,
        discountPrice,
        originalPriceDisplay,
        discountPriceDisplay,
        isDiscounted
      };
    },
    []
  );

  const onBuyClick = async (tier: SubscriptionTier, cadence: Cadence) => {
    const paywallCouponConfig = getPaywallCouponInfo();

    if (!user.shouldOfferPaywallDiscount || !paywallCouponConfig) {
      return;
    }

    const couponSku =
      getCouponSku({
        paywallCouponConfig,
        tier,
        cadence,
        platform: Platform.WEB,
        currentSku: user.getGeoSubOrderSku({ cadence, tier }),
        countryCode: user.activeCountryCode
      }) ?? undefined;

    const couponName = couponSku ? paywallCouponConfig.name : undefined;

    const { originalPriceDisplay, discountPrice } = getCouponPrices(
      paywallCouponConfig,
      tier,
      cadence
    );

    const description = getCouponDiscountDurationMessage({
      paywallCouponConfig,
      originalPriceDisplay,
      currencyCode: user.geoSubCurrencyCode,
      ft: intl.ft,
      cadence,
      tier,
      countryCode: user.activeCountryCode
    });

    await onUpgradeClick(tier, {
      toBeTransferred: user.isGuest,
      couponSku,
      couponName,
      description,
      price: discountPrice
    });
  };

  const onUpgradeClick = async (
    tier: SubscriptionTier,
    stripeCheckoutOpts?: StripeCheckoutOpts
  ) => {
    const { couponName } = stripeCheckoutOpts ?? {};
    // @TODO move this logic to the is-coupon package
    const disableBookkeepingCoupons = ['wbjulyfirstmonth1'];
    const isCouponBookkeepingEligible = !(
      couponName && disableBookkeepingCoupons.includes(couponName)
    );
    if (isEligibleForBookkeepingUpsell() && isCouponBookkeepingEligible) {
      setPendingUpgradeTier(tier);
      setPendingUpgradeOpts(stripeCheckoutOpts);
      setShowBookkeepingModal(true);
      return;
    }

    await executeUpgrade(tier, stripeCheckoutOpts);
  };

  const onBookkeepingAccept = async () => {
    setShowBookkeepingModal(false);
    if (pendingUpgradeTier) {
      await executeUpgrade(pendingUpgradeTier, pendingUpgradeOpts, true);
    }
    setPendingUpgradeTier(null);
    setPendingUpgradeOpts(undefined);
  };

  const onBookkeepingDecline = async () => {
    setShowBookkeepingModal(false);
    if (pendingUpgradeTier) {
      await executeUpgrade(pendingUpgradeTier, pendingUpgradeOpts, false);
    }
    setPendingUpgradeTier(null);
    setPendingUpgradeOpts(undefined);
  };

  const closeBookkeepingModal = () => {
    setShowBookkeepingModal(false);
    setPendingUpgradeTier(null);
    setPendingUpgradeOpts(undefined);
  };

  const getPendingPlanPrice = () => {
    if (!pendingUpgradeTier) return 0;
    const effectiveTier = getBookkeepingEligibleTier(pendingUpgradeTier) ?? pendingUpgradeTier;
    return user.getGeoSubAmount({ tier: effectiveTier, cadence: selectedInterval });
  };

  return {
    switching,
    loadingCheckout,
    onUpgradeClick,
    OriginalPrice,
    FinalPrice,
    isCurrentPlan,
    getCouponPrices,
    onBuyClick,
    // Bookkeeping upsell modal
    showBookkeepingModal,
    closeBookkeepingModal,
    onBookkeepingAccept,
    onBookkeepingDecline,
    pendingPlanPriceCents: getPendingPlanPrice(),
    pendingTier: pendingUpgradeTier
      ? getBookkeepingEligibleTier(pendingUpgradeTier) ?? pendingUpgradeTier
      : null,
    hasBaseSubscription,
    selectedInterval,
    currencyCode: user.geoSubCurrencyCode
  };
};

const getCouponDiscountDurationMessage = ({
  paywallCouponConfig,
  currencyCode,
  originalPriceDisplay,
  ft,
  cadence,
  tier,
  countryCode
}: {
  paywallCouponConfig: PaywallCouponConfig;
  currencyCode: string;
  originalPriceDisplay: number;
  ft: ISIntl['ft'];
  cadence: Cadence;
  tier: SubscriptionTier;
  countryCode: string;
}) => {
  const isPlanDiscounted = !!getCouponDiscount({
    paywallCouponConfig,
    platform: Platform.WEB,
    tier,
    cadence,
    countryCode
  });

  if (!paywallCouponConfig.durationMonths || !isPlanDiscounted) {
    return;
  }

  const isMonthly = cadence === Cadence.MONTHLY;

  const monthlyDurationMessage =
    paywallCouponConfig.durationMonths === 1
      ? ft(messages.subscriptionDiscountDurationMonth)
      : ft(messages.subscriptionDiscountDurationMonthly, {
          months: isMonthly ? paywallCouponConfig.durationMonths : 12
        });

  const description =
    monthlyDurationMessage +
    ' ' +
    ft(messages.subscriptionCouponDesc, {
      price: getIntlCurrency({
        number: originalPriceDisplay,
        currencyCode,
        localeCode: defaultsStore.locale.name
      }),
      cadence: ft(isMonthly ? messages.perMonth : messages.perYear)
    });

  return description;
};

const getBKCouponDiscountDurationMessage = ({
  paywallCouponConfig,
  currencyCode,
  basePlanPrice,
  cadence,
  tier,
  ft
}: {
  paywallCouponConfig?: PaywallCouponConfig;
  basePlanPrice: number;
  tier: SubscriptionTier;
  currencyCode: string;
  cadence: Cadence;
  ft: ISIntl['ft'];
}) => {
  if (!paywallCouponConfig || !paywallCouponConfig.durationMonths) {
    return;
  }

  const { discountedPrice } = getBookkeepingTierPrice(
    tier as BookkeepingTier,
    cadence,
    currencyCode,
    basePlanPrice
  );

  if (!discountedPrice) {
    return;
  }

  const isMonthly = cadence === Cadence.MONTHLY;

  const monthlyDurationMessage =
    paywallCouponConfig.durationMonths === 1
      ? ft(messages.subscriptionDiscountDurationMonth)
      : ft(messages.subscriptionDiscountDurationMonthly, {
          months: isMonthly ? paywallCouponConfig.durationMonths : 12
        });

  const description =
    monthlyDurationMessage +
    ' ' +
    ft(messages.subscriptionCouponDesc, {
      price: getIntlCurrency({
        number: basePlanPrice / 100,
        currencyCode,
        localeCode: defaultsStore.locale.name
      }),
      cadence: ft(isMonthly ? messages.perMonth : messages.perYear)
    });

  return description;
};

function buildCheckoutOptions(
  bookkeepingOrderSku: string | undefined,
  bookkeepingEligibleTier: SubscriptionTier | null,
  selectedInterval: Cadence,
  user: UserModel,
  stripeCheckoutOpts: StripeCheckoutOpts | undefined,
  couponName: string | undefined,
  coupon: PaywallCouponConfig | undefined,
  intl: ISIntl
): StripeCheckoutOpts | undefined {
  const bookkeepingTierPrice = bookkeepingOrderSku
    ? getBookkeepingTierPrice(
        bookkeepingEligibleTier as BookkeepingTier,
        selectedInterval,
        user.geoSubCurrencyCode,
        user.getGeoSubAmount({ tier: bookkeepingEligibleTier!, cadence: selectedInterval })
      )
    : undefined;
  const bookkeepingPrice = bookkeepingTierPrice?.discountedPrice
    ? bookkeepingTierPrice.discountedPrice
    : bookkeepingTierPrice?.regularPrice;

  const checkoutOpts = bookkeepingOrderSku
    ? {
        ...stripeCheckoutOpts,
        orderSku: bookkeepingOrderSku,
        price: bookkeepingPrice,
        couponName: couponName as ISPaywallCoupon | undefined,
        couponSku: stripeCheckoutOpts?.couponSku,
        description: getBKCouponDiscountDurationMessage({
          tier: bookkeepingEligibleTier!,
          paywallCouponConfig: coupon,
          cadence: selectedInterval,
          basePlanPrice: bookkeepingTierPrice?.regularPrice || 0,
          currencyCode: user.geoSubCurrencyCode,
          ft: intl.ft
        })
      }
    : stripeCheckoutOpts;

  return checkoutOpts;
}
